<script setup>
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { STORAGE_API_HOST } from '~/common/utils/api-hosts.js';
import { accessTokenCookie } from '~/common/utils/common.utils';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  dashboard: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'save']);

const { $services, $t, $toast, route, auth_store, common_store } = useCommonImports();
const MAX_HTML_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const bi_store = useBiStore();
const { copy: copyToClipboard } = useClipboard();

const can_share_reports = computed(() => auth_store.check_permission('share_reports', route.params.asset_id));

const form$ = ref(null);
const maps_data = ref(null);

const state = reactive({
  is_loading: false,
  is_generating_link: false,
  is_revoking_link: false,
  share_link_token: props.dashboard?.shared_link?.is_active ? props.dashboard.shared_link.token : null,
  is_link_copied: false,
});

const public_link = computed(() => {
  if (!state.share_link_token)
    return null;
  const api_host = import.meta.env.VITE_APP_API_HOST || '';
  const domain = api_host.replace(/https?:\/\/api\./, '');
  const dashboard_host = `https://dashboard.${domain}`;
  const org_uid = auth_store.current_organization?.uid;
  const dashboard_uid = props.dashboard?.uid;
  const asset_id = props.dashboard?.asset;
  const base_path = asset_id ? `/${asset_id}/bi-dashboard/public` : '/bi-dashboard/public';
  return `${dashboard_host}${base_path}?public_token=${state.share_link_token}&organization=${org_uid}&dashboard=${dashboard_uid}`;
});

const configuration_option_value = computed(() => form$.value?.data?.configuration_option);

const has_existing_html = computed(() =>
  !!form$.value?.data?.html_content && !form$.value?.data?.html_file,
);

async function getMapsData() {
  try {
    const response = await $services.terra_view_service.getAll({
      query: { asset: route.params.asset_id },
    });
    maps_data.value = response.data;
  }
  catch (err) {
    logger.error('Failed to fetch maps data:', err);
  }
}

async function onHtmlFileChange(newValue) {
  if (!newValue) {
    form$.value.el$('html_content').update('');
    return;
  }
  const file = newValue instanceof File ? newValue : newValue?.file;
  if (!file)
    return;
  if (file.size > MAX_HTML_FILE_SIZE_BYTES) {
    $toast({
      title: $t('HTML file too large'),
      text: $t('The maximum file size is 20 MB'),
      type: 'error',
    });
    form$.value.el$('html_file').clear();
    form$.value.el$('html_content').update('');
    return;
  }

  // NOTE: The upload is done manually (without use_uppy) because both the frontend and backend blacklist .html/.htm extensions. We rename to .ehtml before uploading to bypass both.
  const ehtml_name = file.name.replace(/\.html?$/i, '.ehtml');
  const ehtml_file = new File([file], ehtml_name, { type: 'text/plain' });

  try {
    const formData = new FormData();
    formData.append('file', ehtml_file, ehtml_file.name);
    const response = await fetch(`${STORAGE_API_HOST}/upload`, {
      method: 'POST',
      headers: { Authorization: `jwt ${accessTokenCookie()}` },
      body: formData,
    });
    if (!response.ok)
      throw new Error('Upload failed');
    const data = await response.json();
    form$.value.el$('html_content').update(data.location);
  }
  catch {
    $toast({
      title: $t('Upload failed'),
      text: $t('Please try again'),
      type: 'error',
    });
    form$.value.el$('html_file').clear();
    form$.value.el$('html_content').update('');
  }
}

async function generateShareLink() {
  state.is_generating_link = true;
  try {
    const org_uid = auth_store.current_organization?.uid;
    const data = await bi_store.createShareLink(props.dashboard?.uid, org_uid);
    state.share_link_token = data?.token;
  }
  catch {
    $toast({
      title: $t('Failed to generate link'),
      text: $t('Please try again'),
      type: 'error',
    });
  }
  finally {
    state.is_generating_link = false;
  }
}

async function revokeShareLink() {
  state.is_revoking_link = true;
  try {
    const org_uid = auth_store.current_organization?.uid;
    await bi_store.revokeShareLink(props.dashboard?.uid, org_uid);
    state.share_link_token = null;
    state.is_link_copied = false;
  }
  catch {
    $toast({
      title: $t('Failed to revoke link'),
      text: $t('Please try again'),
      type: 'error',
    });
  }
  finally {
    state.is_revoking_link = false;
  }
}

function copyLink() {
  copyToClipboard(public_link.value);
  state.is_link_copied = true;
  setTimeout(() => {
    state.is_link_copied = false;
  }, 2000);
}

function onSave() {
  state.is_loading = true;
  const form_data = form$.value.data;
  const is_standard = form_data.configuration_option === 'standard';
  const is_html = form_data.configuration_option === 'html';

  emit('save', {
    name: form_data.dashboard_name,
    ...(is_standard
      ? {
          standard: true,
          report_data: {
            module: form_data.module,
            data: {
              id: form_data.map,
            },
          },
        }
      : {}),
    ...(is_html
      ? {
          report_data: {
            content: {
              is_html: true,
              html_url: form_data.html_content,
            },
          },
        }
      : {}),
    ...(form_data.assignees_input.assignees_type === 'everyone'
      ? {
          public: true,
          members: {},
        }
      : {
          public: false,
          members: form_data.assignees_input.assignees.reduce((acc, member) => {
            acc[member.uid] = 'read';
            return acc;
          }, {}),
        }),
  });
}

function onFormMounted() {
  if (props.mode === 'edit') {
    const configuration_option
      = props.dashboard?.widgets?.content?.is_html === true
        ? 'html'
        : props.dashboard.standard === true
          ? 'standard'
          : 'custom';
    form$.value.load({
      dashboard_name: props.dashboard.name,
      assignees_input: {
        assignees_type: props.dashboard.public ? 'everyone' : 'custom',
        assignees: Object.keys(props.dashboard.members),
      },
      configuration_option,
      module: props.dashboard?.widgets?.module,
      map: props.dashboard?.widgets?.data?.id,
      html_content: props.dashboard?.widgets?.content?.html_url || '',
    }, true);
  }
}

onMounted(() => {
  if (route.params.asset_id)
    getMapsData();
  state.share_link_token = props.dashboard?.shared_link?.is_active ? props.dashboard.shared_link.token : null;
});
</script>

<template>
  <HawkModalContainer id="bi-dashboard-settings-modal">
    <Vueform
      ref="form$"
      size="sm"
      class="max-w-[600px]"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      :endpoint="onSave"
      :display-errors="false"
      @mounted="onFormMounted"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            <div class="flex flex-col justify-start">
              {{ props.mode === 'edit' ? $t('Edit dashboard') : $t('Create dashboard') }}
            </div>
          </template>
        </HawkModalHeader>
        <HawkModalContent>
          <TextElement
            name="dashboard_name"
            class="w-full"
            :label="$t('Name')"
            rules="required"
          />
          <RadiogroupElement
            v-if="bi_store.scope === 'global'"
            class="mt-4"
            default="custom"
            :label="$t('Type')"
            name="configuration_option"
            :disabled="props.mode === 'edit'"
            :items="[
              { value: 'custom', label: $t('Custom') },
              { value: 'standard', label: $t('Standard') },
              { value: 'html', label: $t('HTML') }
            ]"
          />
          <SelectElement
            :conditions="[['configuration_option', '==', 'standard']]"
            name="module"
            default="maps"
            :can-deselect="false"
            :can-clear="false"
            :native="false"
            :search="true"
            :disabled="props.mode === 'edit'"
            :label="$t('Module')"
            :items="[
              { label: $t('Maps'), value: 'maps' },
              { label: $t('Scan report'), value: 'scan-report' },
            ]"
            class="mt-4"
          />
          <SelectElement
            :conditions="[['configuration_option', '==', 'standard'], ['module', 'in', ['maps', 'scan-report']]]"
            name="map"
            :label="$t('Map')"
            :search="true"
            :disabled="props.mode === 'edit'"
            :items="maps_data"
            label-prop="name"
            value-prop="uid"
            rules="required"
            class="mt-4"
            append-to="#bi-dashboard-settings-modal"
          />
          <FileElement
            v-if="configuration_option_value === 'html'"
            name="html_file"
            :label="$t('HTML Content')"
            :presets="['hawk_file_element']"
            :options="{
              clickable_text: $t('Click to upload'),
              text: $t('or drag and drop'),
              description: $t('HTML files, up to 20 MB'),
            }"
            accept=".html,.htm"
            :use_uppy="false"
            :drop="true"
            :auto="false"
            :submit="false"
            :clickable="false"
            :url="false"
            :rules="[]"
            class="mt-4 col-span-12"
            :description="configuration_option_value === 'html' && has_existing_html ? $t('HTML already uploaded. Choose a new file to replace it.') : ''"
            @change="onHtmlFileChange"
          />
          <HiddenElement
            :conditions="[['configuration_option', '==', 'html']]"
            name="html_content"
            rules="required"
          />
          <ObjectElement
            v-if="can_share_reports"
            class="mt-4"
            name="assignees_input"
            :label="$t('Share with')"
          >
            <RadiogroupElement
              name="assignees_type"
              :items="{
                everyone: $t('Everyone'),
                custom: $t('Custom'),
              }"
              default="everyone"
            />
            <HawkAssigneeInput
              :options="{
                name: 'assignees',
                conditions: [
                  ['assignees_input.assignees_type', 'custom'],
                ],
                has_teams: true,
                placeholder: $t('Select members'),
                columns: {
                  default: { container: 12, label: 12, wrapper: 12 },
                  sm: { container: 12, label: 12, wrapper: 12 },
                  md: { container: 12, label: 12, wrapper: 12 },
                },
                appendTo: '#bi-dashboard-settings-modal',
              }"
              :multi="true"
              :format_load="true"
            />
          </ObjectElement>

          <div v-if="props.mode === 'edit' && configuration_option_value !== 'standard'" class="col-span-12 mt-4">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium text-gray-700">{{ $t('Shareable public link') }}</span>
            </div>

            <div v-if="!state.share_link_token" class="flex items-center gap-2">
              <HawkButton
                type="outlined"
                size="xs"
                :loading="state.is_generating_link"
                @click="generateShareLink"
              >
                <IconHawkLinkExternalOne class="w-4 h-4" />
                {{ $t('Generate') }}
              </HawkButton>
            </div>

            <div v-else class="flex items-center gap-2">
              <HawkButton
                type="outlined"
                size="xs"
                @click="copyLink"
              >
                <IconHawkCheckCircle v-if="state.is_link_copied" class="w-4 h-4" />
                <IconHawkCopyTwo v-else class="w-4 h-4" />
                {{ state.is_link_copied ? $t('Copied!') : $t('Copy') }}
              </HawkButton>
              <HawkButton
                type="outlined"
                color="error"
                size="xs"
                :loading="state.is_revoking_link"
                @click="revokeShareLink"
              >
                <IconHawkXClose class="w-4 h-4" />
                {{ $t('Revoke') }}
              </HawkButton>
            </div>
          </div>
        </HawkModalContent>
        <HawkModalFooter>
          <template #right>
            <div class="flex justify-end w-full col-span-full">
              <HawkButton
                class="mr-4"
                type="outlined"
                @click="emit('close')"
              >
                {{ $t('Cancel') }}
              </HawkButton>
              <ButtonElement
                name="save"
                :loading="state.is_loading"
                submits
              >
                {{ $t('Save') }}
              </ButtonElement>
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
